import {
  pageOffset,
  type ApprovalDetail,
  type ApprovalKind,
  type Me,
  type PoolPosition,
  type PoolPositions,
  type WaitingOnOthers,
} from '@myrewards/contracts';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { useSearchParams } from 'react-router';
import { budgetsQuery, meQuery, waitingQuery } from '../../api';
import { waitingOnOthersQuery } from '../../api/actingFor';
import { actsForExecutivesNow } from '../../acting-for/switches';
import { approvalsQuery } from '../../api/ops';
import { sectorSetupQuery } from '../../api/settings';
import { Hero, PageBody, type HeroStat } from '../../shell';
import {
  Card,
  clientPage,
  DataTable,
  EmptyState,
  formatCount,
  formatMoney,
  organizationDate,
  Pager,
  plural,
  scoped,
  StatusPill,
  useClampedPage,
  usePaging,
} from '../../ui';
import { KIND_LABEL } from '../labels';
import {
  ExecutiveBar,
  KIND_DECISION,
  KIND_FILTER,
  parseKindFilter,
  type ExecutiveKindFilter,
} from './ExecutiveBar';
import {
  decidesOwn,
  emptyApprovals,
  KIND_ORDER,
  listCard,
  names,
  overBudgetPools,
  paaUnsetWords,
  poolStat,
  waitingWords,
  type EmptyApprovalSection,
} from './presenter';
import { othersMeta, othersWhose, sectionsOn } from '../../acting-for/words';
import { OthersSections, type OthersKind } from './OthersSections';
import s from '../Ops.module.css';

const GROUP: Record<ApprovalKind, { title: string; shows: string; canDo: string }> = {
  self_recognition: {
    title: 'Executive decisions',
    shows: 'Awards waiting for an Executive decision.',
    canDo: 'Open one to see who can approve, the people and any top-up required.',
  },
  delegate: {
    title: 'Entries for your OK',
    shows:
      'Awards that need your OK before they go on: entries from delegates who need it, and Sector bonuses from people a System Admin named to start them in your sector.',
    canDo: 'Open one to approve it, return it with a note, or reject it.',
  },
  overage: {
    title: 'Over budget',
    shows: 'Awards that would take a pool below zero, sent to the Executives.',
    canDo: 'Open one to approve it with a top-up (back to zero by default) or decline it.',
  },
  executive: {
    title: 'PAA and TAA',
    shows: 'Achievement awards the Board has decided outside the app, waiting to be recorded here.',
    canDo: 'Open one to match people and amounts to the decision, assign pools, and approve.',
  },
  record_decision: {
    title: 'Executive Discretionary decisions',
    shows: 'Requests the Executives decided outside the app, waiting for their decision and proof.',
    canDo: 'Open one to record what was approved, attach the proof and send it on.',
  },
};

/** An Executive's switch set to a kind with nothing waiting (X2.empty, X3.empty). */
const FILTER_EMPTY: Record<ExecutiveKindFilter, { title: string; sub: string; body: string }> = {
  'over-budget': {
    title: 'No overages to approve',
    sub: 'When an award would take a pool below zero, the Sector Lead can send it to the Executives. It lands here, and any one of you can approve it with a top-up.',
    body: 'When a Sector Lead sends an over-budget award to the Executives, you’ll see the pool’s ledger, the people and the top-up that brings it back to zero here.',
  },
  'paa-taa': {
    title: 'No PAA or TAA waiting',
    sub: 'When a Sector Lead sends a Professional or Team Achievement Award, it lands here for any one Executive to record the Board’s decision.',
    body: 'When one is sent, you’ll see its people, amounts and write-up here, and the pools it draws on.',
  },
  independent: {
    title: 'No Executive decisions waiting',
    sub: 'Pending Executive decisions appear here until an Executive approves or rejects them.',
    body: 'When one is sent, you’ll see who can approve it, the people and any top-up here.',
  },
};

/** The Sector Lead's delegates, by name, when their sector setup is open to them. */
function useDelegates(me: Me): { name: string; needsApproval: boolean; active: boolean }[] | null {
  const sectorId = me.roles.find((r) => r.role === 'sector_lead')?.sector?.id ?? null;
  const setup = useQuery({
    ...sectorSetupQuery(sectorId ?? 'none'),
    enabled: sectorId !== null,
    retry: false,
  });
  return (
    setup.data?.delegates?.map((d) => ({
      name: d.person.name,
      needsApproval: d.needsApproval,
      active: d.active,
    })) ?? null
  );
}

/** A sector's pool among the year's positions (a sector pool's id is its sector's). */
function poolOf(
  positions: PoolPositions | undefined,
  sector: { id: string; name: string } | null,
): PoolPosition | null {
  if (positions === undefined || sector === null) return null;
  return (
    positions.pools.find((p) => p.pool.id === sector.id) ??
    positions.pools.find((p) => p.pool.name === sector.name) ??
    null
  );
}

/** X3.empty: the PAA/TAA pool beside the count, "Not set" with the way to set it until it is. */
function paaPoolStat(pool: PoolPosition): HeroStat {
  if (pool.budget === null || pool.remaining === null)
    return {
      label: scoped(pool.pool.name, `budget ${String(pool.fiscalYear)}`),
      value: 'Not set',
      notSet: {
        message: 'Set it before the first approval, or when you approve one',
        actionLabel: 'Budget pools',
        href: '/manage/executive/budget-pools',
      },
    };
  return poolStat({
    name: pool.pool.name,
    fiscalYear: pool.fiscalYear,
    left: pool.remaining,
    budgetCents: pool.budget.cashCents,
  });
}

/** "All pools · over budget 2026 · 0 · All 5 pools within budget · $665,328 cash left" (X2.empty). */
function overBudgetStat(positions: PoolPositions): HeroStat {
  const set = positions.pools.filter((p) => p.remaining !== null);
  const over = set.filter((p) => (p.remaining?.cashCents ?? 0) < 0);
  const left = set.reduce((sum, p) => sum + Math.max(0, p.remaining?.cashCents ?? 0), 0);
  const unset = positions.pools.length - set.length;
  return {
    label: scoped('All pools', `over budget ${String(positions.fiscalYear)}`),
    value: formatCount(over.length),
    meta: [
      over.length === 0
        ? set.length === 0
          ? 'No budgets set yet'
          : `All ${plural(set.length, 'pool', 'pools')} within budget`
        : `${formatCount(over.length)} of ${plural(set.length, 'pool', 'pools')} over budget`,
      set.length === 0 ? null : `${formatMoney(left)} cash left`,
      unset === 0 ? null : `${formatCount(unset)} not set`,
    ]
      .filter((part): part is string => part !== null)
      .join(' · '),
  };
}

const BUDGET_POOLS_HREF = '/manage/executive/budget-pools';

/** A pool's cash left this year in words: over budget never as a bare negative (APP-070 D3). */
function leftWords(pool: PoolPosition): string {
  if (pool.remaining === null) return 'Not set';
  const left = pool.remaining.cashCents;
  return left < 0 ? `${formatMoney(-left)} over budget` : formatMoney(left);
}

/**
 * X2.empty's day-one facts: every pool's year beside the over-budget filter with nothing waiting.
 * The shield says no pool is over; a pool that is over, with no award waiting on a top-up, is named
 * instead. A budget not set yet is greyed with the way to set it.
 */
function PoolLedgers({ positions }: { positions: PoolPositions }): ReactElement {
  const year = String(positions.fiscalYear);
  const over = positions.pools.filter((p) => (p.remaining?.cashCents ?? 0) < 0);
  const notSet = (
    <span className={s.muted}>
      Not set for {year} ·{' '}
      <Link href={BUDGET_POOLS_HREF} className={s.inlineLink}>
        Set it in Budget pools
      </Link>
    </span>
  );
  return (
    <Card
      title={`Pool ledgers · ${year}`}
      help={{
        shows: `Every pool’s ${year} cash: its budget, what’s been awarded and what’s left.`,
        canDo:
          'Nothing to approve here. When an award would take a pool below zero, its ledger and the top-up that brings it back to zero show here. Budgets are set in Budget pools.',
      }}
      aside={
        <span className={s.muted}>Cash · {plural(positions.pools.length, 'pool', 'pools')}</span>
      }
    >
      {over.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="shield"
          size="sm"
          headline="No pool is over budget"
          body="If one goes over, you’ll see its ledger here: budget, awarded so far, the award, and the top-up that brings it back to zero."
        />
      ) : (
        <EmptyState
          variant="first-day"
          art="pool"
          size="sm"
          headline="No overage waiting on a top-up"
          body={`${names(over.map((p) => p.pool.name))} ${over.length === 1 ? 'is' : 'are'} over budget, with no award waiting on you. When a Sector Lead sends one, you’ll see its ledger here and the top-up that brings it back to zero.`}
        />
      )}
      <DataTable
        caption={`Pool ledgers · ${year}`}
        rows={positions.pools}
        rowKey={(p) => p.pool.id}
        minWidth={560}
        cards
        rowTone={(p) => ((p.remaining?.cashCents ?? 0) < 0 ? 'problem' : null)}
        empty={{
          title: 'No pools yet.',
          body: 'Each pool’s budget, what’s been awarded and what’s left show here.',
          art: 'pool',
        }}
        emptyState={
          <EmptyState
            variant="not-set"
            art="pool"
            size="sm"
            headline="No pools set up yet"
            body="Each pool’s budget, what’s been awarded and what’s left show here once its budget is set."
            action={{ label: 'Budget pools', href: BUDGET_POOLS_HREF }}
          />
        }
        columns={[
          {
            id: 'pool',
            header: 'Pool',
            card: 'title',
            cell: (p) => (
              <>
                <span className={s.strong}>{p.pool.name}</span>
                <span className={s.sub}>
                  {p.budget === null ? notSet : `Set for ${String(p.fiscalYear)}`}
                </span>
              </>
            ),
          },
          {
            id: 'budget',
            header: 'Cash budget',
            numeric: true,
            cell: (p) =>
              p.budget === null ? (
                <span className={s.muted}>Not set</span>
              ) : (
                formatMoney(p.budget.cashCents)
              ),
          },
          {
            id: 'awarded',
            header: 'Awarded',
            numeric: true,
            // Paid and in progress, as Budget pools counts it.
            cell: (p) => formatMoney(p.paid.cashCents + p.inProgress.cashCents),
          },
          {
            id: 'left',
            header: 'Left',
            numeric: true,
            cell: (p) =>
              p.remaining === null ? (
                <span className={s.muted}>Not set</span>
              ) : (
                <span className={s.strong}>{leftWords(p)}</span>
              ),
          },
        ]}
      />
    </Card>
  );
}

/**
 * SL7.empty: a Sector Lead with nothing waiting sees the decision screen they'll use, empty, so
 * the first entry from a delegate who needs their OK looks familiar. The delegates card keeps
 * APP-070 D9's first-day and caught-up words.
 */
function LeadWorkspace({
  section,
  pool,
}: {
  section: EmptyApprovalSection | null;
  pool: PoolPosition | null;
}): ReactElement {
  const left = pool?.remaining ?? null;
  const full =
    pool !== null &&
    left !== null &&
    pool.budget !== null &&
    left.cashCents === pool.budget.cashCents;
  const poolBody =
    pool === null
      ? 'Each entry shows its effect on your pool here before you decide.'
      : left === null
        ? `The ${pool.pool.name} budget for ${String(pool.fiscalYear)} isn’t set yet. An Executive sets it in Budget pools. Each entry shows its effect here before you decide.`
        : left.cashCents < 0
          ? // Over budget in words, never a bare negative (APP-070 D3).
            `The ${pool.pool.name} pool is ${formatMoney(-left.cashCents)} over budget for ${String(pool.fiscalYear)}. An entry that adds to it goes on to the Executives after your OK. Each entry shows its effect here before you decide.`
          : `${full ? 'The full ' : ''}${formatMoney(left.cashCents)} cash and ${plural(left.options, 'option', 'options')} ${full ? 'are available' : `are left for ${String(pool.fiscalYear)}`}. Each entry shows its effect here before you decide.`;
  return (
    <>
      {section !== null && (
        <Card
          title={section.title}
          accent="gold"
          help={{
            shows:
              'Entries that need your OK before they go on: from delegates who need it, and Sector bonuses from people a System Admin named to start them in your sector.',
            canDo:
              'Nothing is waiting. Choose in Sector settings whose entries stop here for your OK.',
          }}
        >
          {section.caughtUp ? (
            <EmptyState
              variant="caught-up"
              headline={section.headline}
              body={section.body}
              {...(section.action !== null && { action: section.action })}
            />
          ) : (
            <EmptyState
              variant="first-day"
              art={section.art}
              headline={section.headline}
              body={section.body}
              {...(section.action !== null && { action: section.action })}
            />
          )}
        </Card>
      )}
      <Card
        title="People"
        help={{
          shows:
            'Each person on an entry, with their cost type, cash and options, and the awards they’ve had in the last 12 months.',
          canDo: 'Nothing to check yet. When an entry is waiting, its people show here.',
        }}
      >
        <DataTable
          caption="People on the entry waiting for you"
          rows={[] as const}
          rowKey={() => ''}
          minWidth={560}
          empty={{
            title: 'No one to review right now.',
            body: 'Each entry lists its people here, with their amounts and their last 12 months of awards.',
          }}
          columns={[
            { id: 'person', header: 'Person', cell: () => null },
            { id: 'type', header: 'Cost type', cell: () => null },
            { id: 'cash', header: 'Cash', numeric: true, cell: () => null },
            { id: 'options', header: 'Options', numeric: true, cell: () => null },
            { id: 'recent', header: 'Last 12 months', cell: () => null },
          ]}
        />
      </Card>
      <div className={s.pair}>
        <Card
          title="Budget impact"
          helpAnchor="approval-budget"
          titleSize="md"
          help={{
            shows: 'What an entry would leave in your pool this year, before you decide.',
            canDo:
              'Nothing is pending. An entry that would go over budget goes on to the Executives after your OK.',
          }}
        >
          <EmptyState
            variant="first-day"
            art="pool"
            size="sm"
            headline="Nothing pending against your pool"
            body={poolBody}
          />
        </Card>
        <Card
          title="From your delegate"
          titleSize="md"
          help={{
            shows: 'The note and files a delegate adds to an entry.',
            canDo: 'Nothing to read yet. You read them here before you decide.',
          }}
        >
          <EmptyState
            variant="first-day"
            art="letter"
            size="sm"
            headline="No note or files yet"
            body="A delegate can add a note and supporting files to each entry. You read them here before you decide."
          />
        </Card>
      </div>
      <Card
        title="Your decision"
        helpAnchor="approval-decision"
        help={{
          shows: 'What happens to an entry next is up to you.',
          canDo: 'Approve it, return it with a note, or reject it with a reason.',
        }}
      >
        <p className={s.muted}>
          Nothing to decide right now. When an entry is waiting you can approve it, return it with a
          note, or reject it.
        </p>
      </Card>
    </>
  );
}

/**
 * One kind of decision waiting, 25 to a page (APP-135). Each group keeps its own page in the
 * address (`overagePage=2`); the rows per page are one choice for all the groups.
 */
function ApprovalGroup({
  kind,
  group,
  asOf,
}: {
  kind: ApprovalKind;
  group: readonly ApprovalDetail[];
  asOf: string;
}): ReactElement {
  const paging = usePaging('approvals', { param: `${kind}Page` });
  const { anchorRef } = paging;
  const paged = clientPage(group, paging.page, paging.size);
  useClampedPage(paging, paged.page.number);
  return (
    <Card
      title={`${GROUP[kind].title} · ${formatCount(group.length)}`}
      help={{ shows: GROUP[kind].shows, canDo: GROUP[kind].canDo }}
      aside={<span className={s.muted}>Oldest first</span>}
    >
      <section ref={anchorRef} tabIndex={-1}>
        <ul className={s.approvalList}>
          {paged.rows.map((d) => {
            const card = listCard(d);
            const over = overBudgetPools(d)[0];
            return (
              <li key={card.id} className={s.approvalItem}>
                <span className={s.approvalHead}>
                  <Link href={`/approvals/${card.id}`} className={s.strongLink}>
                    {card.displayId}
                  </Link>
                  <StatusPill tone="waiting" size="sm" label="Waiting on you" />
                  {over !== undefined && (
                    <StatusPill tone="problem" size="sm" label={`${over.pool.name} over budget`} />
                  )}
                </span>
                <span className={s.cardText}>{card.summary}</span>
                <span className={s.sub}>
                  {card.meta} · {KIND_LABEL[kind]} · {waitingWords(d.queuedAt, asOf)}
                </span>
              </li>
            );
          })}
        </ul>
      </section>
      <Pager
        label={GROUP[kind].title}
        page={paged.page}
        onPage={(n) => {
          paging.setPage(n);
        }}
        onSize={(size) => {
          paging.setSize(size, pageOffset(paged.page));
        }}
      />
    </Card>
  );
}

/**
 * Approvals for a Stock Admin (APP-148 D2, Queue board): "Waiting on others", the Executives' and
 * Sector Leads' work in five sections, after any decisions the reader makes in their own right
 * (an Executive's or a lead's own approvals first). An award listed as their own isn't listed again.
 */
function StockAdminApprovals({
  me,
  own,
  others,
  asOf,
  kinds,
  filter,
}: {
  me: Me;
  own: readonly ApprovalDetail[];
  others: WaitingOnOthers;
  asOf: string;
  kinds: readonly ApprovalKind[];
  /** The Executive's kind switch (`?kind=`): that kind's decisions only. */
  filter: ExecutiveKindFilter | null;
}): ReactElement {
  const skip = new Set(own.map((d) => `award:${d.award.id}`));
  const sections = sectionsOn(others.switches);
  const kind = filter === null ? null : othersKind(filter);
  const rows = others.rows.filter(
    (row) =>
      !skip.has(row.key) &&
      sections.some((x) => x.id === row.section) &&
      (kind === null || row.decision === kind.decision),
  );
  const counts = {
    executives: rows.filter((r) => r.whose.role === 'executive').length,
    sectorLeads: rows.filter((r) => r.whose.role === 'sector_lead').length,
  };
  const whose =
    others.switches.executives && others.switches.sectorLeads
      ? 'the Executives and Sector Leads'
      : others.switches.executives
        ? 'the Executives'
        : 'the Sector Leads';
  const othersStat: HeroStat = {
    // R149: only the roles whose switch is on, in Home's words.
    label: scoped(othersWhose(others.switches), 'waiting'),
    value: formatCount(rows.length),
    meta: othersMeta({ ...others, counts }),
  };
  const overages = own.filter((d) => d.kind === 'overage').length;
  return (
    <>
      <Hero
        eyebrow={`Approvals · for ${whose}${filter === null ? '' : ` · ${KIND_FILTER[filter].label}`}`}
        title={own.length > 0 ? 'Waiting on your decision' : 'Waiting on others'}
        sub={
          own.length > 0
            ? `Your own decisions first, oldest first. Under them, the work that belongs to ${whose}: when one of them asks you to, open it and do it for them.`
            : `Work that belongs to ${whose === 'the Executives and Sector Leads' ? 'the Executives and the Sector Leads' : whose}, oldest first. When one of them asks you to, open it and do it for them. You say who it’s for; files and a note are up to you.`
        }
        back={{ href: '/', label: 'Home', name: 'Back to Home' }}
        stats={[
          ...(own.length > 0
            ? [
                {
                  label: scoped('Approvals', 'waiting on you'),
                  value: formatCount(own.length),
                  meta: `${formatCount(overages)} over budget`,
                },
              ]
            : []),
          othersStat,
        ]}
        titleTestId="approvals-title"
      />
      <ExecutiveBar me={me} current={filter} />
      <PageBody>
        <div className={s.stack}>
          {kinds.map((k) => (
            <ApprovalGroup key={k} kind={k} group={own.filter((d) => d.kind === k)} asOf={asOf} />
          ))}
          <OthersSections others={others} asOf={asOf} skip={skip} kind={kind} />
        </div>
      </PageBody>
    </>
  );
}

/**
 * The Executive's kind switch on a Stock Admin's Waiting on others (APP-148): each kind shows its
 * section and, in it, only the rows waiting on that decision, by the row's own `decision` (R68).
 * An older Executive award shares the PAA and TAA section; its tab has its own title.
 */
function othersKind(filter: ExecutiveKindFilter): OthersKind {
  const decision = KIND_DECISION[filter];
  if (filter !== 'independent')
    return { section: filter === 'over-budget' ? 'over_budget' : 'paa_taa', decision };
  return {
    section: 'paa_taa',
    decision,
    title: 'Executives · Executive decisions',
    empty: 'No older Executive decisions are waiting on the Executives.',
  };
}

/** Everything waiting on this person's decision, grouped by kind, oldest first within each. */
export function ApprovalsPage(): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  const {
    data: { items: listed, history },
  } = useSuspenseQuery(approvalsQuery);
  const [params] = useSearchParams();
  const roles = new Set(me.roles.map((r) => r.role));
  // APP-148: a Stock Admin's list also holds what they may do for an Executive or a Sector Lead.
  // Those are Waiting on others, below; only what the reader decides in their own right is theirs.
  const waiting = listed.filter(decidesOwn);
  const { data: others } = useQuery({
    ...waitingOnOthersQuery,
    enabled: roles.has('stock_admin') && me.preview === null,
    retry: false,
  });
  // An Executive's switch shows one kind at a time (X2, X3), and so does a Stock Admin's while
  // acting for Executives is on (APP-148); everyone else sees every kind.
  const filter =
    roles.has('executive') || actsForExecutivesNow(me) ? parseKindFilter(params.get('kind')) : null;
  const items =
    filter === null
      ? waiting
      : waiting.filter((d) => d.kind !== null && KIND_FILTER[filter].kinds.includes(d.kind));
  // A Sector Lead whose only approving role it is gets SL7's pool hero and the empty workspace
  // when nothing waits. Blended roles keep one card per role (APP-070 D13).
  const leadOnly =
    roles.has('sector_lead') && !roles.has('executive') && !roles.has('executive_delegate');
  const leadSector = me.roles.find((r) => r.role === 'sector_lead')?.sector ?? null;
  const { data: positions } = useQuery({
    ...budgetsQuery,
    enabled: leadOnly || roles.has('executive'),
    retry: false,
  });
  const leadPool = leadOnly ? poolOf(positions, leadSector) : null;
  // X3.empty: an Executive hears when the PAA/TAA budget isn't set before the first one arrives.
  const paaPool = roles.has('executive')
    ? (positions?.pools.find((p) => p.kind === 'paa_taa') ?? null)
    : null;
  const paaUnset = paaPool !== null && paaPool.budget === null ? paaPool.fiscalYear : null;
  const asOf = useQuery(waitingQuery).data?.asOf ?? organizationDate(new Date().toISOString());
  const delegates = useDelegates(me);
  const kinds = KIND_ORDER.filter((kind) => items.some((d) => d.kind === kind));
  const overages = items.filter((d) => d.kind === 'overage').length;
  // APP-274: only a delegate given Executive bonuses has a decision to record.
  const recordsDecisions = me.roles.some(
    (r) => r.role === 'executive_delegate' && r.awardTypes?.includes('EDB') === true,
  );
  const empty = emptyApprovals(
    [...roles].filter((r) => r !== 'executive_delegate' || recordsDecisions),
    delegates,
    history,
    paaUnset,
  );
  // The switch is set to a kind with nothing waiting, while other decisions are.
  const filteredOut = filter !== null && items.length === 0;
  const title = filteredOut
    ? FILTER_EMPTY[filter].title
    : items.length === 0
      ? empty.title
      : 'Waiting on your decision';
  const sub = filteredOut
    ? FILTER_EMPTY[filter].sub
    : items.length === 0
      ? empty.sub
      : 'Oldest first. Open one to see the people, the amounts and the budget, then decide.';
  // With nothing waiting, the pool is the number that matters (SL7.empty). With entries waiting,
  // the count leads: each entry's page shows the pool before and after it.
  const stats: HeroStat[] =
    leadPool !== null && waiting.length === 0
      ? [
          poolStat({
            name: leadPool.pool.name,
            fiscalYear: leadPool.fiscalYear,
            left: leadPool.remaining,
            budgetCents: leadPool.budget?.cashCents ?? null,
          }),
        ]
      : filter === 'over-budget' && items.length === 0 && positions !== undefined
        ? [overBudgetStat(positions)]
        : [
            {
              label:
                filter === null
                  ? 'Approvals · waiting on you'
                  : scoped(KIND_FILTER[filter].label, 'waiting on you'),
              value: formatCount(items.length),
              meta:
                filter !== null
                  ? `${plural(waiting.length, 'decision', 'decisions')} waiting in all`
                  : items.length === 0
                    ? 'Nothing waiting right now'
                    : `${formatCount(overages)} over budget`,
            },
            ...(filter === 'paa-taa' && paaPool !== null ? [paaPoolStat(paaPool)] : []),
          ];
  // D2: Approvals for a Stock Admin is Waiting on others, while either switch is on; with both
  // off the page is as it was. Own decisions (an Executive's, a lead's) come first.
  // An Executive's kind switch shows their own decisions of that kind, as before.
  if (
    others !== undefined &&
    (filter === null || !roles.has('executive')) &&
    (others.switches.executives || others.switches.sectorLeads)
  ) {
    const own = filter === null ? waiting : items;
    return (
      <StockAdminApprovals
        me={me}
        own={own}
        others={others}
        asOf={asOf}
        kinds={KIND_ORDER.filter((kind) => own.some((d) => d.kind === kind))}
        filter={filter}
      />
    );
  }
  return (
    <>
      <Hero
        eyebrow={
          leadOnly && leadSector !== null
            ? `${leadSector.name} · Approvals`
            : filter === null
              ? 'Approvals'
              : `Executive · Approvals · ${KIND_FILTER[filter].label}`
        }
        eyebrowTone={filter === null ? 'cyan' : 'gold'}
        title={title}
        sub={sub}
        stats={stats}
        titleTestId="approvals-title"
      />
      <ExecutiveBar me={me} current={filter} />
      <PageBody>
        <div className={s.stack}>
          {filteredOut && filter === 'over-budget' && positions !== undefined && (
            <PoolLedgers positions={positions} />
          )}
          {filteredOut ? (
            <Card
              title={KIND_FILTER[filter].label}
              help={{
                shows: `${KIND_FILTER[filter].label} decisions waiting on any one Executive.`,
                canDo: 'Switch to another kind above, or see every decision waiting on you.',
              }}
            >
              <EmptyState
                variant="first-day"
                art={filter === 'over-budget' ? 'pool' : 'award'}
                headline={FILTER_EMPTY[filter].title}
                body={[
                  FILTER_EMPTY[filter].body,
                  filter === 'paa-taa' && paaUnset !== null ? paaUnsetWords(paaUnset) : null,
                  waiting.length > 0
                    ? `${plural(waiting.length, 'other decision is', 'other decisions are')} waiting on you.`
                    : null,
                ]
                  .filter((part): part is string => part !== null)
                  .join(' ')}
                action={{ label: 'All approvals', href: '/approvals' }}
              />
            </Card>
          ) : items.length === 0 && leadOnly ? (
            <LeadWorkspace
              section={empty.sections.find((x) => x.key === 'delegate') ?? null}
              pool={leadPool}
            />
          ) : items.length === 0 ? (
            empty.sections.map((section) => (
              <Card
                key={section.key}
                title={section.title}
                help={{
                  shows: 'Every decision waiting on you, from every role you hold, oldest first.',
                  canDo: 'Open one to decide it. Nothing is waiting right now.',
                }}
              >
                {section.caughtUp ? (
                  <EmptyState
                    variant="caught-up"
                    headline={section.headline}
                    body={section.body}
                    {...(section.action !== null && { action: section.action })}
                  />
                ) : (
                  <EmptyState
                    variant="first-day"
                    art={section.art}
                    headline={section.headline}
                    body={section.body}
                    {...(section.action !== null && { action: section.action })}
                  />
                )}
              </Card>
            ))
          ) : (
            kinds.map((kind) => (
              <ApprovalGroup
                key={kind}
                kind={kind}
                group={items.filter((d) => d.kind === kind)}
                asOf={asOf}
              />
            ))
          )}
        </div>
      </PageBody>
    </>
  );
}
